import { Control, DomUtil } from 'leaflet'
import type { ControlOptions, Map as LeafletMap } from 'leaflet'

// 定义比例尺单位类型
type ScaleUnit = 'nautical' | 'kilometer' | 'mile'

// 定义自定义比例尺控件的选项类型
interface CustomScaleOptions extends ControlOptions {
  maxWidth?: number
  units?: ScaleUnit[]
  unitLabels?: { [key in ScaleUnit]: string }
}

// 自定义比例尺控件类
class CustomScale extends Control {
  private _container!: HTMLElement
  private _map!: LeafletMap
  private _maxWidth: number
  private _position: string
  private _units: ScaleUnit[]
  private _unitLabels: { [key in ScaleUnit]: string }

  constructor(options?: CustomScaleOptions) {
    super(options)

    this._maxWidth = options?.maxWidth || 100
    this._position = options?.position || 'bottomleft'
    this._units = options?.units || ['kilometer']
    this._unitLabels = options?.unitLabels || {
      nautical: 'nm',
      kilometer: 'km',
      mile: 'mi',
    }

    this.onAdd = this.onAdd.bind(this)
    this.update = this.update.bind(this)
  }

  override onAdd(map: LeafletMap): HTMLElement {
    this._map = map

    // 创建比例尺容器
    this._container = DomUtil.create(
      'div',
      'leaflet-control-custom-scale leaflet-bar'
    )
    this._container.style.backgroundColor = '#fff'
    this._container.style.boxShadow = '0 1px 5px rgba(0, 0, 0, 0.65)'
    this._container.style.padding = '6px'
    this._container.style.borderRadius = '4px'

    // 设置控件位置
    this.setPosition(this._position)

    // 监听地图缩放事件，触发比例尺更新
    map.on('zoomend', this.update)

    // 初次添加时立即更新比例尺
    this.update()

    return this._container
  }

  override onRemove(map: LeafletMap): void {
    map.off('zoomend', this.update)
  }

  override setPosition(position: string): this {
    if (position !== this._position) {
      this._position = position
      this._map.removeControl(this)
      this._map.addControl(this)
    }
    return this
  }

  update(): void {
    // 清除现有内容
    DomUtil.empty(this._container)

    // 计算当前地图分辨率
    const bounds = this._map.getBounds()
    const centerLat = bounds.getCenter().lat
    const wFactor = 1 / Math.cos((centerLat * Math.PI) / 180)
    const size = this._map.getSize().y
    const resolutions: { [key in ScaleUnit]: number } = {
      nautical:
        this._map.distance(bounds.getNorthWest(), bounds.getSouthEast()) *
        wFactor *
        0.539956803,
      kilometer:
        this._map.distance(bounds.getNorthWest(), bounds.getSouthEast()) *
        wFactor *
        1000,
      mile:
        this._map.distance(bounds.getNorthWest(), bounds.getSouthEast()) *
        wFactor *
        0.621371192,
    }

    // 创建比例尺元素和文本
    for (const unit of this._units) {
      const resolution = resolutions[unit]
      const width = Math.round((size * resolution) / this._maxWidth)
      const text = `${resolution.toFixed(1)} ${this._unitLabels[unit]}`

      const scaleElement = DomUtil.create(
        'div',
        'leaflet-control-custom-scale-line'
      )
      scaleElement.style.width = `${width}px`
      scaleElement.style.height = '10px'
      scaleElement.style.backgroundColor = '#333'
      scaleElement.style.borderRadius = '2px'

      const scaleTextElement = DomUtil.create(
        'div',
        'leaflet-control-custom-scale-text'
      )
      scaleTextElement.innerText = text

      this._container.appendChild(scaleElement)
      this._container.appendChild(scaleTextElement)
    }
  }
}

export default CustomScale

// const map = L.map('mapid').setView([51.505, -0.09], 13)

// L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
//   attribution:
//     '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
// }).addTo(map)

// new CustomScale({
//   position: 'bottomright',
//   units: ['nautical', 'kilometer'],
//   unitLabels: {
//     nautical: 'n.m.',
//     kilometer: 'km'
//   }
// }).addTo(map)
